import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (pw !== pw2) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      if (data.user) setUser(data.user);
      toast.success("Password updated");
      navigate("/account");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || "Could not reset password"); setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-4">
        <div className="text-center" data-testid="reset-no-token">
          <p className="text-muted-foreground">This reset link is missing its token.</p>
          <Link to="/forgot-password" className="text-emerald font-semibold text-sm">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] grid place-items-center px-4 py-12">
      <form onSubmit={submit} className="w-full max-w-md bg-card rounded-2xl border border-border p-7" data-testid="reset-password-page">
        <h1 className="font-display text-2xl font-bold">Choose a new password</h1>
        <p className="text-sm text-muted-foreground mt-1 mb-5">At least 8 characters. We check it against known breached passwords for your safety.</p>
        <div className="space-y-3">
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input type="password" required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" className="w-full rounded-xl border border-border pl-9 pr-4 py-3 text-sm" data-testid="reset-password" />
          </div>
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm new password" className="w-full rounded-xl border border-border pl-9 pr-4 py-3 text-sm" data-testid="reset-password-confirm" />
          </div>
          {error && <p className="text-sm text-coral" data-testid="reset-error">{error}</p>}
          <button disabled={loading} className="w-full bg-ink text-white rounded-xl py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="reset-submit">
            {loading ? <Loader2 size={18} className="animate-spin" /> : null} Update password
          </button>
        </div>
      </form>
    </div>
  );
}
